import React, { useMemo } from "react";
import FormField from "~/components/Project/formField";
import Button from "~/components/ui/button";
import type { CreateProject, UpdateProject } from "~/interface/project";
import styles from "./index.module.scss";

interface ProjectModalContentProps {
  projectData: CreateProject | UpdateProject;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCreate?: () => void;
  handleUpdateProject?: () => void;
  closeModal: () => void;
  isFormValid: boolean;
  isLock: boolean;
  startDateString: string | undefined;
  endDateString: string | undefined;
  buttonLabel: string;
  isEditMode: boolean;
  dateValidationError?: string | null;
}

interface FormFieldConfig {
  label: string;
  name: string;
  type: "text" | "textarea" | "date";
  required: boolean;
  placeholder?: string;
  value: string;
  maxLength?: number;
}

const ProjectModalContent: React.FC<ProjectModalContentProps> = ({
  projectData,
  handleInputChange,
  handleCreate,
  handleUpdateProject,
  closeModal,
  isFormValid,
  isLock,
  startDateString,
  endDateString,
  buttonLabel,
  isEditMode,
  dateValidationError,
}) => {
  const formFields: FormFieldConfig[] = React.useMemo(
    () => [
      {
        label: "プロジェクト名",
        name: "name",
        type: "text",
        required: true,
        value: projectData.name ?? "",
        maxLength: 100,
      },
      {
        label: "説明",
        name: "description",
        type: "textarea",
        required: false,
        value: projectData.description ?? "",
        maxLength: 1000,
      },
      {
        label: "契約開始日",
        name: "start_date",
        type: "date",
        required: false,
        value: startDateString || "",
      },
      {
        label: "契約終了日",
        name: "end_date",
        type: "date",
        required: false,
        value: endDateString || "",
      },
      {
        label: "クライアント名",
        name: "client.name",
        type: "text",
        required: false,
        value: projectData.client.name ?? "",
        maxLength: 100,
      },
      {
        label: "クライアントURL",
        name: "client.url",
        type: "text",
        required: false,
        value: projectData.client.url ?? "",
      },
    ],
    [
      projectData.name,
      projectData.description,
      startDateString,
      endDateString,
      projectData.client.name,
      projectData.client.url,
    ],
  );

  // 文字数制限を超えているかをチェック
  const hasExceededLimits = useMemo(() => {
    return formFields.some((field) => {
      if (field.maxLength && field.value) {
        return field.value.length > field.maxLength;
      }
      return false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formFields]);

  // 文字数制限を考慮したフォームの有効性
  const isFormValidWithCharLimits = isFormValid && !hasExceededLimits;

  const dateFields = formFields.filter((field) => field.type === "date");
  const regularFields = formFields.filter((field) => field.type !== "date");

  return (
    <div>
      <form className={styles["content"]}>
        {regularFields.map((field) => {
          return (
            <FormField
              key={field.name}
              label={field.label}
              name={field.name}
              type={field.type}
              required={field.required}
              value={field.value}
              onChange={handleInputChange}
              {...(field.maxLength && { maxLength: field.maxLength })}
            />
          );
        })}

        {/* Date fields container for side-by-side layout */}
        <div className={styles["dateFieldsContainer"]}>
          {dateFields.map((field) => {
            return (
              <FormField
                key={field.name}
                label={field.label}
                name={field.name}
                type={field.type}
                required={field.required}
                value={field.value}
                onChange={handleInputChange}
                {...(field.maxLength && { maxLength: field.maxLength })}
              />
            );
          })}
        </div>
        {dateValidationError && <div className={styles["errorText"]}>{dateValidationError}</div>}
      </form>

      <div className={styles["buttonContainer"]}>
        <Button label="キャンセル" styleType="white" onClick={closeModal} />
        <Button
          label={buttonLabel}
          styleType="blue"
          {...((isEditMode ? handleUpdateProject : handleCreate) && {
            onClick: isEditMode ? handleUpdateProject : handleCreate,
          })}
          disabled={!isFormValidWithCharLimits || isLock}
        />
      </div>
    </div>
  );
};

export default ProjectModalContent;
